import { useSendNotification } from "@app/hooks/useNotification";
import { useNovuClient } from "@app/hooks/useNovuClient";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useConversationNotificationPreferences } from "@app/lib/swr/notifications";
import { useSlackNotifications, useUserMetadata } from "@app/lib/swr/user";
import { setUserMetadataFromClient } from "@app/lib/user";
import type {
  NotificationCondition,
  NotificationPreferencesDelay,
} from "@app/types/notification_preferences";
import {
  CONVERSATION_NOTIFICATION_METADATA_KEYS,
  DEFAULT_NOTIFICATION_CONDITION,
  DEFAULT_NOTIFICATION_DELAY,
  isNotificationCondition,
  isNotificationPreferencesDelay,
  makeNotificationPreferencesUserMetadata,
  NOTIFICATION_CONDITION_OPTIONS,
  NOTIFICATION_DELAY_OPTIONS,
} from "@app/types/notification_preferences";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SettingsList,
  SliderToggle,
} from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
import i18n from "i18next";
import cloneDeep from "lodash/cloneDeep";
import { useEffect, useState } from "react";
import type { Control } from "react-hook-form";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const NOTIFICATION_PREFERENCES_DELAY_LABELS: Record<
  NotificationPreferencesDelay,
  string
> = {
  "5_minutes": i18n.t("settings.delayEvery5Min", "Every 5 minutes"),
  "15_minutes": i18n.t("settings.delayEvery15Min", "Every 15 minutes"),
  "30_minutes": i18n.t("settings.delayEvery30Min", "Every 30 minutes"),
  "1_hour": i18n.t("settings.delayEveryHour", "Every hour"),
  daily: i18n.t("settings.delayADay", "Once a day"),
  weekly: i18n.t("settings.delayAWeek", "Once a week"),
};

const NOTIFICATION_CONDITION_LABELS: Record<NotificationCondition, string> = {
  all_messages: i18n.t("settings.notifyAllActivity", "All activity"),
  only_mentions: i18n.t("settings.notifyWhenMentioned", "Mentions only"),
  never: i18n.t("settings.notifyNever", "Nothing"),
};

const NotificationPreferencesFormSchema = z.object({
  notifyCondition: z.enum(NOTIFICATION_CONDITION_OPTIONS),
  emailDelay: z.enum(NOTIFICATION_DELAY_OPTIONS),
  inApp: z.boolean(),
  slack: z.boolean(),
  email: z.boolean(),
});

type NotificationPreferencesFormValues = z.infer<
  typeof NotificationPreferencesFormSchema
>;

export function useNotificationPreferencesForm({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { hasFeature } = useFeatureFlags();

  // Novu is optional per deployment — without it these workflow preferences
  // have nothing to configure, so disable the underlying fetches and let
  // consumers hide the block via isNovuConfigured.
  const { isNovuConfigured } = useNovuClient();
  const effectiveDisabled = disabled || !isNovuConfigured;

  const hasSlackNotificationsFeature = hasFeature(
    "conversations_slack_notifications"
  );
  const { canConfigureSlack, isSlackSetupLoading } = useSlackNotifications(
    owner.sId,
    { disabled: effectiveDisabled || !hasSlackNotificationsFeature }
  );
  const displaySlackOption = hasSlackNotificationsFeature && canConfigureSlack;

  const { conversationPreferences, status, saveConversationPreferences } =
    useConversationNotificationPreferences({
      owner,
      disabled: effectiveDisabled,
    });

  const {
    metadata: conversationEmailMetadata,
    mutateMetadata: mutateConversationEmailDelay,
  } = useUserMetadata(makeNotificationPreferencesUserMetadata("email"));
  const {
    metadata: notifyConditionMetadata,
    mutateMetadata: mutateNotifyCondition,
  } = useUserMetadata(CONVERSATION_NOTIFICATION_METADATA_KEYS.notifyCondition);

  const form = useForm<NotificationPreferencesFormValues>({
    resolver: zodResolver(NotificationPreferencesFormSchema),
    defaultValues: {
      notifyCondition: DEFAULT_NOTIFICATION_CONDITION,
      emailDelay: DEFAULT_NOTIFICATION_DELAY,
      inApp: false,
      slack: false,
      email: false,
    },
  });

  useEffect(() => {
    if (form.formState.isDirty || !conversationPreferences) {
      return;
    }
    form.reset({
      notifyCondition: isNotificationCondition(notifyConditionMetadata?.value)
        ? notifyConditionMetadata.value
        : DEFAULT_NOTIFICATION_CONDITION,
      emailDelay: isNotificationPreferencesDelay(
        conversationEmailMetadata?.value
      )
        ? conversationEmailMetadata.value
        : DEFAULT_NOTIFICATION_DELAY,
      inApp: Boolean(conversationPreferences.channels.in_app),
      slack: Boolean(conversationPreferences.channels.chat),
      email: Boolean(conversationPreferences.channels.email),
    });
  }, [
    conversationPreferences,
    conversationEmailMetadata,
    notifyConditionMetadata,
    form,
  ]);

  const save = async (): Promise<boolean> => {
    let succeeded = false;
    await form.handleSubmit(async (data) => {
      try {
        const { dirtyFields } = form.formState;
        if (
          conversationPreferences &&
          (dirtyFields.inApp || dirtyFields.slack || dirtyFields.email)
        ) {
          const updatedPreference = cloneDeep(conversationPreferences);
          updatedPreference.channels.in_app = data.inApp;
          updatedPreference.channels.chat = data.slack;
          updatedPreference.channels.email = data.email;
          await saveConversationPreferences(updatedPreference);
        }
        if (dirtyFields.emailDelay) {
          await setUserMetadataFromClient({
            key: makeNotificationPreferencesUserMetadata("email"),
            value: data.emailDelay,
          });
          await mutateConversationEmailDelay();
        }
        if (dirtyFields.notifyCondition) {
          await setUserMetadataFromClient({
            key: CONVERSATION_NOTIFICATION_METADATA_KEYS.notifyCondition,
            value: data.notifyCondition,
          });
          await mutateNotifyCondition();
        }
        form.reset(data);
        succeeded = true;
      } catch (error) {
        sendNotification({
          type: "error",
          title: t(
            "settings.errorUpdatingNotificationPreferences",
            "Error updating notification preferences"
          ),
          description: normalizeError(error).message,
        });
      }
    })();
    return succeeded;
  };

  return {
    control: form.control,
    displaySlackOption,
    isDirty: form.formState.isDirty,
    isLoading:
      isNovuConfigured && (status === "loading" || isSlackSetupLoading),
    isNovuConfigured,
    save,
    status,
    workflowEnabled: Boolean(conversationPreferences?.enabled),
  };
}

interface NotificationPreferencesProps {
  control: Control<NotificationPreferencesFormValues>;
  displaySlackOption: boolean;
  workflowEnabled: boolean;
}

export function NotificationPreferences({
  control,
  displaySlackOption,
  workflowEnabled,
}: NotificationPreferencesProps) {
  const { t } = useTranslation();
  const { isNovuConfigured } = useNovuClient();
  const { field: notifyConditionField } = useController({
    name: "notifyCondition",
    control,
  });
  const { field: emailDelayField } = useController({
    name: "emailDelay",
    control,
  });
  const { field: inAppField } = useController({ name: "inApp", control });
  const { field: slackField } = useController({ name: "slack", control });
  const { field: emailField } = useController({ name: "email", control });

  const [portalContainer] = useState<HTMLElement | undefined>(() =>
    typeof document !== "undefined" ? document.body : undefined
  );

  const notificationsDisabled = notifyConditionField.value === "never";
  const isInAppEnabled = inAppField.value && workflowEnabled;
  const isSlackEnabled = slackField.value && workflowEnabled;
  const isEmailEnabled = emailField.value && workflowEnabled;
  const isEmailFrequencyEnabled = isEmailEnabled && !notificationsDisabled;

  // Novu is optional per deployment — without it these workflow preferences
  // have nothing to configure, so hide the block instead of erroring.
  if (!isNovuConfigured) {
    return null;
  }

  return (
    <SettingsList>
      <SettingsList.Row
        title={t("settings.notifyMeAboutTitle", "Notify me about")}
        description={t(
          "settings.notifyMeAboutDescription",
          "Choose which activity sends you a notification"
        )}
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                isSelect
                label={
                  NOTIFICATION_CONDITION_LABELS[notifyConditionField.value]
                }
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent mountPortalContainer={portalContainer}>
              {NOTIFICATION_CONDITION_OPTIONS.map((condition) => (
                <DropdownMenuItem
                  key={condition}
                  label={NOTIFICATION_CONDITION_LABELS[condition]}
                  onClick={() => notifyConditionField.onChange(condition)}
                />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <SettingsList.Row
        title={t("settings.inAppPopupTitle", "In-app popup")}
        description={t(
          "settings.inAppPopupDescription",
          "Show a popup inside Dasis"
        )}
        action={
          <SliderToggle
            selected={isInAppEnabled}
            disabled={notificationsDisabled}
            onClick={() => inAppField.onChange(!isInAppEnabled)}
          />
        }
      />

      {displaySlackOption && (
        <SettingsList.Row
          title="Slack"
          description={t(
            "settings.slackChannelDescription",
            "A direct message in Slack"
          )}
          action={
            <SliderToggle
              selected={isSlackEnabled}
              disabled={notificationsDisabled}
              onClick={() => slackField.onChange(!isSlackEnabled)}
            />
          }
        />
      )}

      <SettingsList.Row
        title={t("settings.emailChannelTitle", "Email")}
        description={t(
          "settings.emailChannelDescription",
          "Receive a summary by email"
        )}
        action={
          <SliderToggle
            selected={isEmailEnabled}
            disabled={notificationsDisabled}
            onClick={() => emailField.onChange(!isEmailEnabled)}
          />
        }
      />

      <SettingsList.Row
        title={t("settings.emailFrequencyTitle", "Email frequency")}
        description={t(
          "settings.emailFrequencyDescription",
          "We'll never email you more often than this"
        )}
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                isSelect
                disabled={!isEmailFrequencyEnabled}
                label={
                  NOTIFICATION_PREFERENCES_DELAY_LABELS[emailDelayField.value]
                }
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent mountPortalContainer={portalContainer}>
              {NOTIFICATION_DELAY_OPTIONS.map((delay) => (
                <DropdownMenuItem
                  key={delay}
                  label={NOTIFICATION_PREFERENCES_DELAY_LABELS[delay]}
                  onClick={() => emailDelayField.onChange(delay)}
                />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
    </SettingsList>
  );
}
